import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw, KeyRound, ShieldCheck } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';

type CheckStatus = 'ok' | 'warn' | 'fail' | 'info';
interface Check { key: string; label: string; status: CheckStatus; detail: string }
interface AccountReadiness {
  account_id: string;
  label: string;
  login?: string;
  registrar_type: string;
  registrar_name: string;
  status: 'ready' | 'at_risk' | 'blocked';
  checks: Check[];
  held_domains: number;
  due_30d: number;
  due_7d: number;
  next_expiry: string | null;
  balance: number | null;
  estimated_cost: number | null;
  currency: string | null;
  checked_at: string;
  renew_capable: boolean;
  needs_payment_confirmation: boolean;
  payment_confirmed_at: string | null;
  payment_confirmed_by: string | null;
  payment_note: string | null;
}
interface NoApiAccount { id: string; label: string; login?: string; registrar_name: string; credential_source: string; held_domains: number; recent: number; missing: string[] }
interface Data {
  accounts: AccountReadiness[];
  accounts_without_api: NoApiAccount[];
  due_days: number;
  payment_confirmation_days: number;
  running: boolean;
}

const STATUS_META = {
  blocked: { label: "Can't renew", tone: 'bg-red-50 text-red-700', icon: XCircle },
  at_risk: { label: 'At risk', tone: 'bg-amber-50 text-amber-700', icon: AlertTriangle },
  ready: { label: 'Ready', tone: 'bg-green-50 text-green-700', icon: CheckCircle2 },
} as const;

const CHECK_ICON: Record<CheckStatus, { icon: typeof Info; tone: string }> = {
  ok: { icon: CheckCircle2, tone: 'text-green-600' },
  warn: { icon: AlertTriangle, tone: 'text-amber-600' },
  fail: { icon: XCircle, tone: 'text-red-600' },
  info: { icon: Info, tone: 'text-slate-400' },
};

const money = (n: number | null, cur: string | null) => (n === null ? null : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${n.toFixed(2)}`);

/** Admin page: per registrar account, what it needs so its domains actually renew. */
export default function RenewalReadinessPage() {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetchApi<ApiResponse<Data>>('/renewal-readiness');
      if (r.data) setData(r.data);
    } catch (e) {
      toast.error('Could not load renewal readiness', e instanceof Error ? e.message : undefined);
    }
  }, [fetchApi, toast]);

  useEffect(() => { load(); }, [load]);
  // While a full check runs in the background, refresh every few seconds.
  useEffect(() => {
    if (!data?.running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data?.running, load]);

  const checkAll = async () => {
    try {
      await fetchApi('/renewal-readiness/check', { method: 'POST', body: '{}' });
      toast.success('Checking every account', 'Prices are quoted without charging; this takes a minute or two.');
      setData((d) => (d ? { ...d, running: true } : d));
    } catch (e) {
      toast.error('Could not start the check', e instanceof Error ? e.message : undefined);
    }
  };

  const recheck = async (id: string) => {
    setBusy(id);
    try {
      await fetchApi('/renewal-readiness/check', { method: 'POST', body: JSON.stringify({ account_id: id }) });
      await load();
    } catch (e) {
      toast.error('Check failed', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const confirmPayment = async (id: string) => {
    setBusy(id);
    try {
      await fetchApi(`/renewal-readiness/${encodeURIComponent(id)}/confirm-payment`, { method: 'POST', body: JSON.stringify({ note }) });
      toast.success('Payment method marked as confirmed', `Valid for ${data?.payment_confirmation_days ?? 90} days.`);
      setConfirming(null); setNote('');
      await load();
    } catch (e) {
      toast.error('Could not save', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const counts = useMemo(() => {
    const c = { blocked: 0, at_risk: 0, ready: 0, due: 0 };
    for (const a of data?.accounts ?? []) { c[a.status]++; c.due += a.due_30d; }
    return c;
  }, [data]);

  if (!data) return <div className="space-y-6"><PageHeader title="Renewal readiness" /><SkeletonCard lines={4} /><SkeletonCard lines={6} /></div>;

  const shown = data.accounts.filter((a) => !onlyProblems || a.status !== 'ready');
  const lastChecked = data.accounts.reduce<string | null>((m, a) => (!m || a.checked_at > m ? a.checked_at : m), null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Renewal readiness"
        description={`Whether each registrar account can actually renew its domains: working API credentials, the renew API accepting it, money on the account (or a confirmed payment method), auto-renew on what's due in ${data.due_days} days, and auto-renewals that didn't go through. Checked daily after the 06:00 sync; prices come from quotes that don't charge.`}
        meta={lastChecked ? <span>Last checked {formatDistanceToNowStrict(new Date(lastChecked), { addSuffix: true })}</span> : undefined}
        actions={
          <button className="btn btn-secondary" onClick={checkAll} disabled={data.running}>
            <RefreshCw className={`mr-2 h-4 w-4 ${data.running ? 'animate-spin' : ''}`} />{data.running ? 'Checking…' : 'Check all now'}
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {([['blocked', counts.blocked], ['at_risk', counts.at_risk], ['ready', counts.ready]] as const).map(([k, n]) => {
          const m = STATUS_META[k];
          return (
            <div key={k} className="card p-4">
              <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.tone}`}><m.icon className="h-3.5 w-3.5" />{m.label}</div>
              <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{n}</div>
              <div className="text-xs text-slate-500">account{n === 1 ? '' : 's'}</div>
            </div>
          );
        })}
        <div className="card p-4">
          <div className="text-xs font-medium text-slate-500">Due in {data.due_days} days</div>
          <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{counts.due}</div>
          <Link to={`/domains?expiring_days=${data.due_days}`} className="text-xs font-medium text-brand-600 hover:underline">View domains</Link>
        </div>
      </div>

      {data.accounts_without_api.length > 0 && (
        <div className="card overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><KeyRound className="h-4 w-4 text-amber-600" />Accounts that can't sync ({data.accounts_without_api.length})</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              No working API credentials, so their domains don't sync and can't be renewed from here. Add the fields below to the account's 1Password item — the 1Password import picks them up before the next sync
              (or use <strong>Refresh from 1Password</strong> on the <Link to="/registrars" className="text-brand-600 hover:underline">Registrars</Link> page). Deactivate an account there if it's a login you never want synced.
            </p>
          </div>
          {(() => {
            const row = (a: NoApiAccount) => (
              <li key={a.id} className="flex flex-col gap-1 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">{a.registrar_name} · {a.label}{a.login && a.login !== a.label && <code className="ml-1.5 text-[11px] font-normal text-slate-400">{a.login}</code>}</div>
                  <div className="text-xs text-slate-500">{a.credential_source === '1password' ? '1Password item' : 'Stored credentials'} · {a.held_domains ? `${a.held_domains} domain${a.held_domains === 1 ? '' : 's'}` : 'no domains known'}{a.recent ? ' · added recently' : ''}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {a.missing.map((f) => <code key={f} className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">{f}</code>)}
                </div>
              </li>
            );
            // Accounts holding domains or added recently alert; older login-only ones don't.
            const alerting = data.accounts_without_api.filter((a) => a.held_domains > 0 || a.recent);
            const quiet = data.accounts_without_api.filter((a) => !(a.held_domains > 0 || a.recent));
            return (
              <>
                {alerting.length > 0 && <ul className="divide-y divide-slate-100">{alerting.map(row)}</ul>}
                {quiet.length > 0 && (
                  <details className="border-t border-slate-100">
                    <summary className="cursor-pointer px-5 py-3 text-sm text-slate-600 hover:bg-slate-50">
                      {quiet.length} older login-only account{quiet.length === 1 ? '' : 's'} with no known domains (bidding logins and the like) — not alerted on
                    </summary>
                    <ul className="divide-y divide-slate-100">{quiet.map(row)}</ul>
                  </details>
                )}
              </>
            );
          })()}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">Accounts holding domains</h2>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />Only problems
        </label>
      </div>

      {data.accounts.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Not checked yet" description="Run the check to see whether each account can renew its domains." />
      ) : shown.length === 0 ? (
        <EmptyState icon={CheckCircle2} title="Every account is ready" description="Untick “Only problems” to see them all." />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {shown.map((a) => {
            const m = STATUS_META[a.status];
            return (
              <div key={a.account_id} className={`card p-5 ${a.status === 'blocked' ? 'border-red-200' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-slate-900">{a.registrar_name} · {a.label}{a.login && a.login !== a.label && <code className="ml-1.5 text-xs font-normal text-slate-400">{a.login}</code>}</h3>
                    <p className="text-xs text-slate-500">
                      {a.held_domains} domain{a.held_domains === 1 ? '' : 's'}
                      {a.due_30d > 0 && <> · <span className={a.due_7d ? 'font-medium text-red-700' : 'text-slate-700'}>{a.due_30d} due in {data.due_days} days{a.due_7d ? ` (${a.due_7d} within 7)` : ''}</span></>}
                      {a.balance !== null && <> · balance {money(a.balance, a.currency)}</>}
                      {a.estimated_cost !== null && <> · ~{money(a.estimated_cost, a.currency)} due</>}
                    </p>
                  </div>
                  <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.tone}`}><m.icon className="h-3.5 w-3.5" />{m.label}</span>
                </div>
                <ul className="mt-3 space-y-2">
                  {a.checks.map((c) => {
                    const ci = CHECK_ICON[c.status];
                    return (
                      <li key={c.key} className="flex gap-2 text-sm">
                        <ci.icon className={`mt-0.5 h-4 w-4 shrink-0 ${ci.tone}`} />
                        <div className="min-w-0"><span className="font-medium text-slate-800">{c.label}.</span> <span className="break-words text-slate-600">{c.detail}</span></div>
                      </li>
                    );
                  })}
                </ul>
                {confirming === a.account_id ? (
                  <div className="mt-4 space-y-2 rounded-md bg-slate-50 p-3">
                    <label className="label" htmlFor={`note-${a.account_id}`}>What did you check? (optional)</label>
                    <input id={`note-${a.account_id}`} className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
                      placeholder={a.registrar_type === 'godaddy' ? 'e.g. Good as Gold topped up to $500' : 'e.g. company card on file, valid to 2028'} />
                    <div className="flex gap-2">
                      <button className="btn btn-primary" disabled={busy === a.account_id} onClick={() => confirmPayment(a.account_id)}>Confirm</button>
                      <button className="btn btn-secondary" onClick={() => { setConfirming(null); setNote(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button className="btn btn-secondary" disabled={busy !== null || data.running} onClick={() => recheck(a.account_id)}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${busy === a.account_id ? 'animate-spin' : ''}`} />Re-check
                    </button>
                    {a.needs_payment_confirmation && (
                      <button className="btn btn-secondary" onClick={() => { setConfirming(a.account_id); setNote(a.payment_note ?? ''); }}>
                        <ShieldCheck className="mr-2 h-4 w-4" />Payment method checked
                      </button>
                    )}
                    <span className="ml-auto text-xs text-slate-400">checked {formatDistanceToNowStrict(new Date(a.checked_at), { addSuffix: true })}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
